import { useEffect, useRef, useState } from "react";

import { Button } from "../../components/Button";
import { InfoText, submitButtonStyles } from "./TmpOnboardingPage.styles";

export const RETURN_DELAY_SECONDS = 10;

/** True when TMP shows the page inside its own (EHR) page, in an iframe. */
export function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Reading window.top across origins can throw: that is framed too.
    return true;
  }
}

/**
 * Where the whole window goes back to. The embedding page is on TMP's origin,
 * so it cannot be reloaded from here and its address cannot be read; the
 * browser only tells us the referrer (by default just TMP's origin) and the
 * origins of the frames above us.
 */
export function returnTarget(): string | null {
  const origins = window.location.ancestorOrigins;
  const topOrigin =
    origins && origins.length > 0 ? origins[origins.length - 1] : null;

  const { referrer } = document;
  if (/^https:\/\//i.test(referrer)) {
    const sameTop = !topOrigin || new URL(referrer).origin === topOrigin;
    if (sameTop) return referrer;
  }
  return topOrigin;
}

/**
 * Tells the embedding page the details were saved (TMP can listen for it and
 * refresh itself), then sends the whole window to `target`. Returns false
 * when the browser refused the navigation, e.g. without a recent click.
 */
export function returnToEmbeddingPage(target: string): boolean {
  try {
    // Nothing sensitive in it, so any origin may read it.
    window.parent.postMessage(
      { source: "patientmpower", type: "tmp-onboarding-completed" },
      "*"
    );
  } catch {
    // The message is a courtesy; the navigation below is what matters.
  }

  try {
    window.top!.location.href = target;
    return true;
  } catch {
    return false;
  }
}

interface IReturnCountdownProps {
  /** Test seams; the page uses the defaults. */
  framed?: boolean;
  target?: string | null;
  goTo?: (target: string) => boolean;
}

/**
 * After the HCP saved the form inside TMP's page: count down, then reload the
 * whole window on TMP's side so the order shows its new state, instead of
 * leaving the HCP on our success screen inside the frame. Outside a frame
 * (the link opened in its own tab) there is nothing to return to.
 */
export function ReturnCountdown({
  framed = isFramed(),
  target = returnTarget(),
  goTo = returnToEmbeddingPage,
}: IReturnCountdownProps) {
  const [secondsLeft, setSecondsLeft] = useState(RETURN_DELAY_SECONDS);
  const [blocked, setBlocked] = useState(false);
  const triedRef = useRef(false);
  const canReturn = framed && Boolean(target);

  useEffect(() => {
    if (!canReturn) return undefined;
    const timer = setInterval(
      () => setSecondsLeft((s) => Math.max(0, s - 1)),
      1000
    );
    return () => clearInterval(timer);
  }, [canReturn]);

  useEffect(() => {
    if (!canReturn || secondsLeft > 0 || triedRef.current) return;
    triedRef.current = true;
    if (!goTo(target!)) setBlocked(true);
  }, [canReturn, secondsLeft, goTo, target]);

  if (!canReturn) {
    return <InfoText>You can close this window.</InfoText>;
  }

  let status = "Returning to the order...";
  if (secondsLeft > 0) {
    status = `Returning to the order in ${secondsLeft} second${
      secondsLeft === 1 ? "" : "s"
    }...`;
  } else if (blocked) {
    status =
      "The page could not return by itself. Use the button below, or close this window.";
  }

  return (
    <>
      <InfoText data-testid="tmp-return-countdown" aria-live="polite">
        {status}
      </InfoText>
      <Button
        type="button"
        label="Return now"
        className={submitButtonStyles()}
        onClick={() => {
          triedRef.current = true;
          if (!goTo(target!)) setBlocked(true);
        }}
      />
    </>
  );
}
